import { Directive, DirectiveBinding } from 'vue'
import './loading.css'
// 引入类型扩展
import '../types/loading.d.ts'

interface LoadingOptions {
  fullscreen?: boolean
  text?: string
  background?: string
}

const LOADING_MIN_HEIGHT = '100px'

const createLoadingElement = (options: LoadingOptions = {}): HTMLElement => {
  const loadingEl = document.createElement('div')
  loadingEl.className = options.fullscreen ? 'v-loading-fullscreen' : 'v-loading'

  // 创建加载动画元素
  const spinner = document.createElement('div')
  spinner.className = 'v-loading-spinner'

  // 创建加载圆环
  const circular = document.createElement('div')
  circular.className = 'v-loading-circular'

  // 创建文字提示
  if (options.text) {
    const text = document.createElement('div')
    text.className = 'v-loading-text'
    text.textContent = options.text
    loadingEl.appendChild(text)
  }

  // 设置背景色
  if (options.background) {
    loadingEl.style.backgroundColor = options.background
  }

  // 组合元素
  spinner.appendChild(circular)
  loadingEl.appendChild(spinner)

  return loadingEl
}

const resolveBinding = (binding: DirectiveBinding): { active: boolean; options: LoadingOptions } => {
  const value = binding.value
  if (typeof value === 'object' && value !== null) {
    return { active: true, options: value }
  }
  return { active: Boolean(value), options: { fullscreen: false } }
}

const applyLoadingMinHeight = (el: HTMLElement) => {
  if (el._loading_min_height_applied || el.offsetHeight >= 100) return
  el._loading_original_min_height = el.style.minHeight
  el.style.minHeight = LOADING_MIN_HEIGHT
  el._loading_min_height_applied = true
}

const restoreLoadingMinHeight = (el: HTMLElement) => {
  if (!el._loading_min_height_applied) return
  el.style.minHeight = el._loading_original_min_height ?? ''
  delete el._loading_original_min_height
  el._loading_min_height_applied = false
}

const removeLoadingInstances = (el: HTMLElement) => {
  if (el._loading_instance) {
    el.removeChild(el._loading_instance)
    delete el._loading_instance
  }

  if (el._loading_fullscreen_instance) {
    document.body.removeChild(el._loading_fullscreen_instance)
    document.body.style.overflow = ''
    delete el._loading_fullscreen_instance
  }
}

const showLoading = (el: HTMLElement, options: LoadingOptions) => {
  removeLoadingInstances(el)

  if (options.fullscreen) {
    restoreLoadingMinHeight(el)
  } else {
    const position = getComputedStyle(el).position
    if (position === 'static' || position === '') {
      el.style.position = 'relative'
    }
    applyLoadingMinHeight(el)
  }

  const loadingEl = createLoadingElement(options)
  if (options.fullscreen) {
    document.body.appendChild(loadingEl)
    document.body.style.overflow = 'hidden'
    el._loading_fullscreen_instance = loadingEl
    return
  }

  el.appendChild(loadingEl)
  el._loading_instance = loadingEl
}

const hideLoading = (el: HTMLElement) => {
  removeLoadingInstances(el)
  restoreLoadingMinHeight(el)
}

const vLoading: Directive = {
  mounted(el: HTMLElement, binding: DirectiveBinding) {
    const { active, options } = resolveBinding(binding)
    if (active) showLoading(el, options)
  },

  updated(el: HTMLElement, binding: DirectiveBinding) {
    if (binding.value === binding.oldValue) return

    const { active, options } = resolveBinding(binding)
    if (active) {
      showLoading(el, options)
      return
    }
    hideLoading(el)
  },

  unmounted(el: HTMLElement) {
    hideLoading(el)
  },
}

export default vLoading
